import React, { useEffect, useRef } from "react";
import styled from "styled-components";

const NeonGridBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        let W = window.innerWidth;
        let H = window.innerHeight;
        canvas.width  = W;
        canvas.height = H;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let animating = true;
        let t         = 0;

        // ── Forward drive ─────────────────────────────────────────────────────
        // driveOffset accumulates continuously — creates the "road" illusion.
        // Lines cycle from horizon → edge and loop back, like lane markings
        // passing under a car.
        let driveOffset = 0;
        const DRIVE_SPEED = 0.05; // world units per frame — tweak for faster/slower

        // ── Scroll wave ───────────────────────────────────────────────────────
        let lastScrollY  = typeof window !== "undefined" ? window.scrollY : 0;
        let waveEnergy   = 0;
        let waveDir      = 1;

        // Mouse
        let mouseX = 0.5, mouseY = 0.5;
        let mxFast = 0.5, myFast = 0.5;
        let mxSlow = 0.5, mySlow = 0.5;
        let glowX  = 0.5, glowY  = 0.5;

        const getIsDark = () => document.body.classList.contains("dark-mode");

        const DARK = {
            bg:        "#02010a",
            grid:      (a) => `rgba(238,14,166,${a})`,
            spire:     (a) => `rgba(238,14,166,${a})`,
            spireNode: (a) => `rgba(255,80,200,${a})`,
            scanline:  "rgba(238,14,166,0.012)",
            sun:       (a) => `rgba(238,14,166,${a})`,
            glow:      (a) => `rgba(238,14,166,${a})`,
        };

        const LIGHT = {
            bg:        null,
            grid:      (a) => `rgba(150,6,90,${a * 0.70})`,
            spire:     (a) => `rgba(140,5,85,${a * 0.65})`,
            spireNode: (a) => `rgba(190,10,115,${a * 0.65})`,
            scanline:  "rgba(150,6,90,0.016)",
            sun:       (a) => `rgba(170,8,100,${a * 0.60})`,
            glow:      (a) => `rgba(160,8,100,${a * 0.55})`,
        };

        const SPIRE_COUNT = 22;
        const spires = Array.from({ length: SPIRE_COUNT }, (_, i) => ({
            f:     i / SPIRE_COUNT,
            baseH: 14 + Math.sin(i * 13.7) * 10 + Math.cos(i * 8.2) * 8,
            phase: i * 5.0,
        }));

        // ── Draw one half — horizontal lines with drive + wave ────────────────
        const drawHalf = (horizon, driveOff, wavePhase, waveAmp, C, isDark, direction) => {
            const edge   = direction === 1 ? H : 0;
            const halfH  = Math.abs(edge - horizon);
            const HLINES = 28;

            for (let i = 0; i < HLINES; i++) {
                // Drive offset: shift each line's 0..1 position by driveOff, then wrap.
                // This makes lines flow continuously from horizon to edge and loop.
                const rawF    = ((i / HLINES) + driveOff) % 1.0;
                const fPow    = Math.pow(rawF, 1.5); // perspective compression

                const baseY   = horizon + (edge - horizon) * fPow;
                if (direction === 1  && baseY > H + 2) continue;
                if (direction === -1 && baseY < -2)    continue;

                // Wave displacement on top of drive
                const waveOff = fPow * waveAmp * direction
                    * Math.sin(wavePhase - fPow * 6.0);

                const y = baseY + waveOff;

                // Opacity — lines near edge are brightest (closest to viewer)
                // Lines near horizon are faint (far away)
                const a = isDark
                    ? 0.02 + fPow * 0.30
                    : 0.015 + fPow * 0.24;

                // Line width — thicker = closer
                const lw = 0.2 + fPow * 1.2;

                ctx.strokeStyle = C.grid(a);
                ctx.lineWidth   = lw;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(W, y);
                ctx.stroke();
            }
        };

        const draw = () => {
            if (!animating) return;
            requestAnimationFrame(draw);
            t += 0.008;

            // Advance drive — wraps automatically via % in drawHalf
            driveOffset = (driveOffset + DRIVE_SPEED * 0.008) % 1.0;

            // Scroll wave
            const nowScroll = window.scrollY;
            const scrollDelta = nowScroll - lastScrollY;
            lastScrollY = nowScroll;

            if (Math.abs(scrollDelta) > 0.5) {
                waveEnergy = Math.min(1.0, waveEnergy + Math.abs(scrollDelta) * 0.018);
                waveDir    = scrollDelta > 0 ? 1 : -1;
            }
            waveEnergy = Math.max(0, waveEnergy * 0.965);

            const wavePhase = t * 3.2 * (waveEnergy > 0.01 ? 1 : 0);
            const waveAmp   = waveEnergy * 22 * waveDir;

            // Mouse easing
            mxFast += (mouseX - mxFast) * 0.10;
            myFast += (mouseY - myFast) * 0.10;
            mxSlow += (mouseX - mxSlow) * 0.038;
            mySlow += (mouseY - mySlow) * 0.038;
            glowX  += (mouseX - glowX)  * 0.055;
            glowY  += (mouseY - glowY)  * 0.055;

            const isDark = getIsDark();
            const C = isDark ? DARK : LIGHT;

            if (isDark) { ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H); }
            else          ctx.clearRect(0, 0, W, H);

            const vx      = W * (0.5 + (mxFast - 0.5) * 0.36);
            const horizon = H * (0.50 + (mySlow - 0.5) * 0.08);

            // ── Bottom half ──────────────────────────────────────────────────
            drawHalf(horizon, driveOffset, wavePhase, waveAmp, C, isDark, 1);

            // ── Top half (mirrored, clipped) ──────────────────────────────────
            ctx.save();
            ctx.beginPath();
            ctx.rect(0, 0, W, horizon);
            ctx.clip();
            drawHalf(horizon, driveOffset, wavePhase, waveAmp, C, isDark, -1);
            ctx.restore();

            // ── Horizon line ─────────────────────────────────────────────────
            ctx.strokeStyle = C.grid(0.32 + waveEnergy * 0.20);
            ctx.lineWidth   = 0.9 + waveEnergy * 0.8;
            ctx.beginPath(); ctx.moveTo(0, horizon); ctx.lineTo(W, horizon); ctx.stroke();

            // ── Spires ───────────────────────────────────────────────────────
            const spireHeightMul = 1.0 + Math.abs(mySlow - 0.5) * 0.8;

            spires.forEach((sp) => {
                const x = sp.f * W
                    + (vx - W * 0.5) * (0.5 - Math.abs(sp.f - 0.5)) * 0.4
                    + Math.sin(sp.phase + t * 0.22) * W * 0.006;

                const h = sp.baseH * spireHeightMul * (1 + waveEnergy * 1.2)
                          + Math.sin(sp.phase + t * 0.18) * 3
                          + Math.sin(sp.phase + wavePhase) * waveEnergy * 4;

                const dist = Math.abs(sp.f - 0.5);
                const a    = isDark
                    ? 0.07 + dist * 0.22 + waveEnergy * 0.12
                    : 0.05 + dist * 0.13 + waveEnergy * 0.08;

                ctx.lineWidth = 0.6 + waveEnergy * 0.4;

                ctx.strokeStyle = C.spire(a);
                ctx.beginPath(); ctx.moveTo(x, horizon); ctx.lineTo(x, horizon + h); ctx.stroke();
                ctx.beginPath(); ctx.arc(x, horizon + h, 1.0 + waveEnergy * 0.6, 0, Math.PI * 2);
                ctx.fillStyle = C.spireNode(Math.min(a * 1.5, 0.85)); ctx.fill();

                ctx.strokeStyle = C.spire(a * 0.70);
                ctx.lineWidth   = 0.5 + waveEnergy * 0.3;
                ctx.beginPath(); ctx.moveTo(x, horizon); ctx.lineTo(x, horizon - h); ctx.stroke();
                ctx.beginPath(); ctx.arc(x, horizon - h, 0.9 + waveEnergy * 0.5, 0, Math.PI * 2);
                ctx.fillStyle = C.spireNode(Math.min(a * 1.2, 0.75)); ctx.fill();

                if (h > 22) {
                    const barW = 3.5 + waveEnergy * 2;
                    const off  = h * 0.35;
                    ctx.lineWidth = 0.4;
                    ctx.strokeStyle = C.spire(a * 0.38);
                    [horizon + off, horizon - off].forEach(by => {
                        ctx.beginPath(); ctx.moveTo(x - barW, by); ctx.lineTo(x + barW, by); ctx.stroke();
                    });
                }
            });

            // ── Horizon glow ─────────────────────────────────────────────────
            const glowRX = W * (0.30 + waveEnergy * 0.10);
            const glowRY = H * (0.15 + waveEnergy * 0.06);
            const gi     = isDark ? 0.18 + waveEnergy * 0.14 : 0.14 + waveEnergy * 0.10;

            ctx.save();
            ctx.translate(vx, horizon);
            ctx.scale(1, glowRY / glowRX);
            const sg = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRX);
            sg.addColorStop(0,   C.sun(gi));
            sg.addColorStop(0.4, C.sun(gi * 0.45));
            sg.addColorStop(1,   C.sun(0));
            ctx.fillStyle = sg;
            ctx.beginPath(); ctx.arc(0, 0, glowRX, 0, Math.PI * 2); ctx.fill();
            ctx.restore();

            // ── Mouse glow ───────────────────────────────────────────────────
            const mgx = glowX * W;
            const mgy = glowY * H;
            const mgR = isDark ? 260 : 200;
            const mgI = isDark ? 0.12 : 0.08;

            ctx.save();
            const mg = ctx.createRadialGradient(mgx, mgy, 0, mgx, mgy, mgR);
            mg.addColorStop(0,    C.glow(mgI));
            mg.addColorStop(0.35, C.glow(mgI * 0.55));
            mg.addColorStop(0.70, C.glow(mgI * 0.18));
            mg.addColorStop(1,    C.glow(0));
            ctx.fillStyle = mg;
            ctx.fillRect(0, 0, W, H);
            ctx.restore();

            // ── Scanlines ────────────────────────────────────────────────────
            for (let y = 0; y < H; y += 3) {
                ctx.fillStyle = C.scanline;
                ctx.fillRect(0, y, W, 1);
            }

            // ── Center vignette ───────────────────────────────────────────────
            if (isDark) {
                const mask = ctx.createRadialGradient(W/2, H/2, 0, W/2, H/2, W * 0.42);
                mask.addColorStop(0,   "rgba(2,1,10,0.44)");
                mask.addColorStop(0.5, "rgba(2,1,10,0.20)");
                mask.addColorStop(1,   "rgba(2,1,10,0)");
                ctx.fillStyle = mask;
                ctx.fillRect(0, 0, W, H);
            }
        };

        const onResize = () => {
            W = window.innerWidth; H = window.innerHeight;
            canvas.width = W; canvas.height = H;
        };
        const onMouse = (e) => {
            mouseX = e.clientX / window.innerWidth;
            mouseY = e.clientY / window.innerHeight;
        };

        window.addEventListener("resize",    onResize);
        window.addEventListener("mousemove", onMouse);
        draw();

        return () => {
            animating = false;
            window.removeEventListener("resize",    onResize);
            window.removeEventListener("mousemove", onMouse);
        };
    }, []);

    return <Canvas ref={canvasRef} aria-hidden="true" />;
};

export default NeonGridBackground;

const Canvas = styled.canvas`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw !important;
    height: 100vh !important;
    pointer-events: none;
    z-index: 0;
    transition: opacity 0.5s ease;

    body.dark-mode  & { opacity: 1;    }
    body.light-mode & { opacity: 0.55; }

    @media (prefers-reduced-motion: reduce) {
        display: none;
    }
`;